import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, API_BASE } from "../../lib/api";

const card = "rounded-2xl border border-adminborder bg-admincard";
const darkInput = "w-full rounded-lg border border-adminborder bg-adminbg px-3.5 py-2.5 text-sm text-slate-200 outline-none focus:border-saffron";

export default function AdminCms() {
  const [cms, setCms] = useState(null);
  const [saving, setSaving] = useState(false);
  const [anns, setAnns] = useState([]);
  const [annForm, setAnnForm] = useState({ title: "", text: "", image_url: "", link: "", active: true });
  const [posts, setPosts] = useState([]);
  const emptyPost = { title: "", category: "", body: "", image_url: "", published: true };
  const [postForm, setPostForm] = useState(emptyPost);
  const [editingPost, setEditingPost] = useState(null);
  const [logoPath, setLogoPath] = useState("");
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoSaving, setLogoSaving] = useState(false);
  const [siteLogoPath, setSiteLogoPath] = useState("");
  const [siteLogoUploading, setSiteLogoUploading] = useState(false);
  const [siteLogoSaving, setSiteLogoSaving] = useState(false);

  const loadBlog = () => api.get("/admin/blog").then((r) => setPosts(r.data)).catch(() => {});

  const loadAnns = () => api.get("/admin/announcements").then((r) => setAnns(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/admin/cms").then((r) => setCms(r.data)).catch(() => {});
    api.get("/shop/admin/brand-logo").then((r) => setLogoPath(r.data.image_path || "")).catch(() => {});
    api.get("/shop/admin/site-logo").then((r) => setSiteLogoPath(r.data.image_path || "")).catch(() => {});
    loadAnns();
    loadBlog();
  }, []);
  if (!cms) return <p className="py-20 text-center text-slate-500">Loading CMS…</p>;

  const hero = cms.hero || {};
  const pages = cms.pages || {};
  const FOOTER_PAGES = [["domestic", "Domestic Courier"], ["international", "International Courier"], ["ecommerce", "eCommerce Shipping"],
                        ["business", "Business Courier"], ["nri", "NRI Courier"],
                        ["about", "About Us"], ["careers", "Careers"], ["contact", "Contact Us"],
                        ["terms", "Terms & Conditions"], ["privacy", "Privacy Policy"], ["refund", "Refund Policy"],
                        ["news", "News & Blog"], ["shipping", "Shipping Policy"], ["prohibited", "Prohibited Items"]];
  const faqs = cms.faqs || [];
  const testimonials = cms.testimonials || [];

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/admin/cms", cms);
      setCms(res.data);
      toast.success("Website content updated");
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const updateList = (key, i, field, value) => {
    const list = [...(cms[key] || [])];
    list[i] = { ...list[i], [field]: value };
    setCms({ ...cms, [key]: list });
  };

  const addAnn = async () => {
    if (!annForm.text.trim()) { toast.error("Announcement text is required"); return; }
    try {
      await api.post("/admin/announcements", annForm);
      toast.success("Announcement published to portal strip");
      setAnnForm({ title: "", text: "", image_url: "", link: "", active: true });
      loadAnns();
    } catch (err) { toast.error(apiError(err)); }
  };
  const toggleAnn = async (a) => {
    try { await api.put(`/admin/announcements/${a.id}`, { active: !a.active }); loadAnns(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const delAnn = async (id) => {
    try { await api.delete(`/admin/announcements/${id}`); loadAnns(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const savePost = async () => {
    if (!postForm.title.trim() || !postForm.body.trim()) { toast.error("Title and content are required"); return; }
    try {
      if (editingPost) await api.put(`/admin/blog/${editingPost}`, postForm);
      else await api.post("/admin/blog", postForm);
      toast.success(editingPost ? "Article updated" : "Article published");
      setPostForm(emptyPost);
      setEditingPost(null);
      loadBlog();
    } catch (err) { toast.error(apiError(err)); }
  };
  const editPost = (p) => {
    setEditingPost(p.id);
    setPostForm({ title: p.title, category: p.category || "", body: p.body, image_url: p.image_url || "", published: p.published });
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  };
  const togglePost = async (p) => {
    try { await api.put(`/admin/blog/${p.id}`, { published: !p.published }); loadBlog(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const delPost = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    try { await api.delete(`/admin/blog/${id}`); loadBlog(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div data-testid="admin-cms" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h1 className="font-heading text-2xl font-bold tracking-tight text-white sm:text-3xl">Website CMS</h1>
        <button onClick={save} disabled={saving} data-testid="cms-save-btn"
                className="rounded-full bg-saffron px-6 py-2.5 text-sm font-bold text-white hover:bg-saffron-hover disabled:opacity-60">
          {saving ? "Saving…" : "Publish Changes"}
        </button>
      </div>

      <div className={`${card} p-6`}>
        <h3 className="font-heading text-lg font-bold text-white">Website Logo (Navbar & Footer)</h3>
        <p className="mt-1 text-xs text-slate-500">Shown on the homepage header, footer and login pages. When set, it replaces the default INDIAFAST icon + text.</p>
        <div className="mt-4 flex flex-wrap items-center gap-4">
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-adminborder px-4 py-2.5 text-xs font-semibold text-slate-400 hover:border-saffron hover:text-saffron" data-testid="site-logo-upload">
            {siteLogoUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {siteLogoPath ? "Logo uploaded ✓ (click to replace)" : "Upload website logo"}
            <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              setSiteLogoUploading(true);
              try {
                const fd = new FormData(); fd.append("file", file);
                const res = await api.post("/uploads", fd);
                setSiteLogoPath(res.data.path);
                toast.success("Logo uploaded — click Save to apply");
              } catch (err) { toast.error(apiError(err)); }
              finally { setSiteLogoUploading(false); }
            }} />
          </label>
          {siteLogoPath && (
            <img src={`${API_BASE}/api/files/${siteLogoPath}?auth=${localStorage.getItem("if_token")}`} alt="Website logo"
                 className="h-12 w-auto max-w-[200px] rounded-lg border border-adminborder bg-white object-contain p-1" data-testid="site-logo-preview" />
          )}
          <button disabled={siteLogoSaving || siteLogoUploading} data-testid="site-logo-save"
                  onClick={async () => {
                    setSiteLogoSaving(true);
                    try { await api.put("/shop/admin/site-logo", { image_path: siteLogoPath }); toast.success("Website logo applied"); }
                    catch (err) { toast.error(apiError(err)); }
                    finally { setSiteLogoSaving(false); }
                  }}
                  className="flex items-center gap-1.5 rounded-lg bg-saffron px-4 py-2 text-xs font-bold text-white hover:bg-saffron-hover disabled:opacity-60">
            {siteLogoSaving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Save Website Logo
          </button>
          {siteLogoPath && (
            <button onClick={async () => {
                      try { await api.put("/shop/admin/site-logo", { image_path: "" }); setSiteLogoPath(""); toast.success("Reset to default INDIAFAST logo"); }
                      catch (err) { toast.error(apiError(err)); }
                    }}
                    data-testid="site-logo-reset"
                    className="rounded-lg border border-adminborder px-3 py-2 text-xs font-semibold text-slate-400 hover:text-white">
              Reset to default
            </button>
          )}
        </div>
        <p className="mt-3 rounded-lg bg-saffron/10 px-3 py-2 text-[11px] font-semibold text-saffron" data-testid="site-logo-size-hint">
          Recommended size: 320 × 96 px wide PNG (transparent background) — square 512 × 512 px also works · max 5 MB
        </p>
      </div>

      <div className={`${card} p-6`}>
        <h3 className="font-heading text-lg font-bold text-white">Document Logo (PDFs & Labels)</h3>
        <p className="mt-1 text-xs text-slate-500">This logo prints on all documents — domestic & international invoices, receipts, manifests and shipper labels.</p>
        <div className="mt-4 flex flex-wrap items-center gap-4">
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-adminborder px-4 py-2.5 text-xs font-semibold text-slate-400 hover:border-saffron hover:text-saffron" data-testid="brand-logo-upload">
            {logoUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {logoPath ? "Logo uploaded ✓ (click to replace)" : "Upload logo"}
            <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              setLogoUploading(true);
              try {
                const fd = new FormData(); fd.append("file", file);
                const res = await api.post("/uploads", fd);
                setLogoPath(res.data.path);
                toast.success("Logo uploaded — click Save to apply");
              } catch (err) { toast.error(apiError(err)); }
              finally { setLogoUploading(false); }
            }} />
          </label>
          {logoPath && (
            <img src={`${API_BASE}/api/files/${logoPath}?auth=${localStorage.getItem("if_token")}`} alt="Brand logo"
                 className="h-14 w-14 rounded-xl border border-adminborder bg-white object-contain p-1" data-testid="brand-logo-preview" />
          )}
          <button disabled={logoSaving || logoUploading} data-testid="brand-logo-save"
                  onClick={async () => {
                    setLogoSaving(true);
                    try { await api.put("/shop/admin/brand-logo", { image_path: logoPath }); toast.success("Logo applied to all documents"); }
                    catch (err) { toast.error(apiError(err)); }
                    finally { setLogoSaving(false); }
                  }}
                  className="flex items-center gap-1.5 rounded-lg bg-saffron px-4 py-2 text-xs font-bold text-white hover:bg-saffron-hover disabled:opacity-60">
            {logoSaving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Save Logo
          </button>
        </div>
        <p className="mt-3 rounded-lg bg-saffron/10 px-3 py-2 text-[11px] font-semibold text-saffron" data-testid="brand-logo-size-hint">
          Recommended size: 512 × 512 px square PNG (transparent background) · JPG/WebP also accepted · max 5 MB
        </p>
      </div>

      <div className={`${card} p-6`}>
        <h3 className="font-heading text-lg font-bold text-white">Homepage Hero</h3>
        <div className="mt-4 space-y-3">
          <input className={darkInput} data-testid="cms-hero-headline" value={hero.headline || ""}
                 onChange={(e) => setCms({ ...cms, hero: { ...hero, headline: e.target.value } })} placeholder="Hero headline" />
          <textarea rows={3} className={darkInput} data-testid="cms-hero-subtext" value={hero.subtext || ""}
                    onChange={(e) => setCms({ ...cms, hero: { ...hero, subtext: e.target.value } })} placeholder="Hero supporting text" />
        </div>
      </div>

      <div className={`${card} p-6`}>
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-lg font-bold text-white">FAQs</h3>
          <button onClick={() => setCms({ ...cms, faqs: [...faqs, { q: "", a: "" }] })} data-testid="cms-add-faq-btn"
                  className="flex items-center gap-1.5 rounded-lg border border-adminborder px-3 py-1.5 text-xs font-bold text-slate-300 hover:border-saffron hover:text-saffron">
            <Plus className="h-3.5 w-3.5" /> Add FAQ
          </button>
        </div>
        <div className="mt-4 space-y-4">
          {faqs.map((f, i) => (
            <div key={i} className="rounded-xl border border-adminborder p-4">
              <div className="flex gap-3">
                <input className={darkInput} data-testid={`cms-faq-q-${i}`} value={f.q} placeholder="Question"
                       onChange={(e) => updateList("faqs", i, "q", e.target.value)} />
                <button onClick={() => setCms({ ...cms, faqs: faqs.filter((_, j) => j !== i) })} data-testid={`cms-faq-del-${i}`}
                        className="rounded-lg border border-adminborder px-3 text-red-400 hover:border-red-500"><Trash2 className="h-4 w-4" /></button>
              </div>
              <textarea rows={2} className={`${darkInput} mt-3`} data-testid={`cms-faq-a-${i}`} value={f.a} placeholder="Answer"
                        onChange={(e) => updateList("faqs", i, "a", e.target.value)} />
            </div>
          ))}
        </div>
      </div>

      <div className={`${card} p-6`}>
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-lg font-bold text-white">Testimonials</h3>
          <button onClick={() => setCms({ ...cms, testimonials: [...testimonials, { name: "", company: "", quote: "", rating: 5 }] })}
                  data-testid="cms-add-testimonial-btn"
                  className="flex items-center gap-1.5 rounded-lg border border-adminborder px-3 py-1.5 text-xs font-bold text-slate-300 hover:border-saffron hover:text-saffron">
            <Plus className="h-3.5 w-3.5" /> Add Testimonial
          </button>
        </div>
        <div className="mt-4 space-y-4">
          {testimonials.map((t, i) => (
            <div key={i} className="grid gap-3 rounded-xl border border-adminborder p-4 sm:grid-cols-2">
              <input className={darkInput} data-testid={`cms-testi-name-${i}`} value={t.name} placeholder="Name"
                     onChange={(e) => updateList("testimonials", i, "name", e.target.value)} />
              <input className={darkInput} data-testid={`cms-testi-company-${i}`} value={t.company} placeholder="Company"
                     onChange={(e) => updateList("testimonials", i, "company", e.target.value)} />
              <textarea rows={2} className={`${darkInput} sm:col-span-2`} data-testid={`cms-testi-quote-${i}`} value={t.quote} placeholder="Quote"
                        onChange={(e) => updateList("testimonials", i, "quote", e.target.value)} />
            </div>
          ))}
        </div>
      </div>

      <div className={`${card} p-6`} data-testid="cms-socials-section">
        <h3 className="font-heading text-lg font-bold text-white">Social Media Links <span className="text-xs font-normal text-slate-500">— footer icons become active links when a URL is set; leave blank to hide</span></h3>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {[["facebook", "Facebook URL"], ["instagram", "Instagram URL"], ["twitter", "Twitter / X URL"],
            ["linkedin", "LinkedIn URL"], ["youtube", "YouTube URL"]].map(([k, ph]) => (
            <input key={k} className={darkInput} data-testid={`social-${k}`} placeholder={ph}
                   value={(cms.socials || {})[k] || ""}
                   onChange={(e) => setCms({ ...cms, socials: { ...(cms.socials || {}), [k]: e.target.value } })} />
          ))}
        </div>
      </div>

      <div className={`${card} p-6`} data-testid="cms-pages-section">
        <h3 className="font-heading text-lg font-bold text-white">Website Pages <span className="text-xs font-normal text-slate-500">— services + footer pages shown publicly at /p/…</span></h3>
        <div className="mt-4 space-y-4">
          {FOOTER_PAGES.map(([slug, label]) => (
            <div key={slug} className="rounded-xl border border-adminborder p-4">
              <input className={darkInput} data-testid={`cms-page-${slug}-title`} value={pages[slug]?.title || label} placeholder={`${label} title`}
                     onChange={(e) => setCms({ ...cms, pages: { ...pages, [slug]: { ...(pages[slug] || {}), title: e.target.value } } })} />
              <textarea rows={4} className={`${darkInput} mt-3`} data-testid={`cms-page-${slug}-body`} value={pages[slug]?.body || ""} placeholder={`${label} content…`}
                        onChange={(e) => setCms({ ...cms, pages: { ...pages, [slug]: { ...(pages[slug] || { title: label }), body: e.target.value } } })} />
            </div>
          ))}
        </div>
      </div>

      <div className={`${card} p-6`}>
        <h3 className="font-heading text-lg font-bold text-white">Announcement Strip <span className="text-xs font-normal text-slate-500">— shown below the portal menu for agents & customers</span></h3>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <input className={darkInput} data-testid="ann-title" placeholder="Title (e.g. Diwali Offer)" value={annForm.title} onChange={(e) => setAnnForm({ ...annForm, title: e.target.value })} />
          <input className={darkInput} data-testid="ann-link" placeholder="Link URL (optional)" value={annForm.link} onChange={(e) => setAnnForm({ ...annForm, link: e.target.value })} />
          <textarea rows={2} className={`${darkInput} sm:col-span-2`} data-testid="ann-text" placeholder="Announcement text shown in the strip…" value={annForm.text} onChange={(e) => setAnnForm({ ...annForm, text: e.target.value })} />
          <input className={`${darkInput} sm:col-span-2`} data-testid="ann-image" placeholder="Image URL (optional)" value={annForm.image_url} onChange={(e) => setAnnForm({ ...annForm, image_url: e.target.value })} />
        </div>
        <button onClick={addAnn} data-testid="ann-add-btn" className="mt-3 rounded-lg bg-saffron px-5 py-2 text-sm font-bold text-white hover:bg-saffron-hover">Publish Announcement</button>
        <div className="mt-4 space-y-2">
          {anns.map((a) => (
            <div key={a.id} data-testid={`ann-item-${a.id}`} className="flex items-center gap-3 rounded-xl border border-adminborder p-3">
              {a.image_url ? <img src={a.image_url} alt="" className="h-9 w-9 rounded-lg object-cover" /> : null}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-white">{a.title || a.text}</p>
                <p className="truncate text-xs text-slate-400">{a.text}</p>
              </div>
              <button onClick={() => toggleAnn(a)} data-testid={`ann-toggle-${a.id}`}
                      className={`rounded-full px-3 py-1 text-[10px] font-bold ${a.active ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-500/15 text-slate-400"}`}>
                {a.active ? "ACTIVE" : "HIDDEN"}
              </button>
              <button onClick={() => delAnn(a.id)} data-testid={`ann-del-${a.id}`} className="rounded-lg border border-adminborder p-2 text-red-400 hover:border-red-500">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          {!anns.length && <p className="py-4 text-center text-xs text-slate-500">No announcements yet.</p>}
        </div>
      </div>

      <div className={`${card} p-6`} data-testid="cms-blog-section">
        <h3 className="font-heading text-lg font-bold text-white">Blog / News Articles <span className="text-xs font-normal text-slate-500">— published articles appear as cards on the News page (/p/news)</span></h3>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <input className={darkInput} data-testid="blog-title" placeholder="Article title" value={postForm.title} onChange={(e) => setPostForm({ ...postForm, title: e.target.value })} />
          <input className={darkInput} data-testid="blog-category" placeholder="Category (e.g. International Shipping)" value={postForm.category} onChange={(e) => setPostForm({ ...postForm, category: e.target.value })} />
          <input className={`${darkInput} sm:col-span-2`} data-testid="blog-image" placeholder="Image URL (optional)" value={postForm.image_url} onChange={(e) => setPostForm({ ...postForm, image_url: e.target.value })} />
          <textarea rows={4} className={`${darkInput} sm:col-span-2`} data-testid="blog-body" placeholder="Article content…" value={postForm.body} onChange={(e) => setPostForm({ ...postForm, body: e.target.value })} />
        </div>
        <div className="mt-3 flex items-center gap-3">
          <button onClick={savePost} data-testid="blog-save-btn" className="rounded-lg bg-saffron px-5 py-2 text-sm font-bold text-white hover:bg-saffron-hover">
            {editingPost ? "Update Article" : "Publish Article"}
          </button>
          {editingPost && (
            <button onClick={() => { setEditingPost(null); setPostForm(emptyPost); }} data-testid="blog-cancel-btn"
                    className="rounded-lg border border-adminborder px-4 py-2 text-xs font-bold text-slate-300">Cancel Edit</button>
          )}
        </div>
        <div className="mt-4 space-y-2">
          {posts.map((p) => (
            <div key={p.id} data-testid={`blog-item-${p.id}`} className="flex items-center gap-3 rounded-xl border border-adminborder p-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-white">{p.title}</p>
                <p className="truncate text-xs text-slate-400">{p.category || "General"} · {(p.created_at || "").slice(0, 10)}</p>
              </div>
              <button onClick={() => editPost(p)} data-testid={`blog-edit-${p.id}`}
                      className="rounded-lg border border-blue-500/40 px-3 py-1 text-[10px] font-bold text-blue-400 hover:bg-blue-500/10">EDIT</button>
              <button onClick={() => togglePost(p)} data-testid={`blog-toggle-${p.id}`}
                      className={`rounded-full px-3 py-1 text-[10px] font-bold ${p.published ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-500/15 text-slate-400"}`}>
                {p.published ? "LIVE" : "HIDDEN"}
              </button>
              <button onClick={() => delPost(p.id)} data-testid={`blog-del-${p.id}`} className="rounded-lg border border-adminborder p-2 text-red-400 hover:border-red-500">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          {!posts.length && <p className="py-4 text-center text-xs text-slate-500">No articles yet.</p>}
        </div>
      </div>
    </div>
  );
}
